/**
 * The four groups the station list reports status in.
 *
 * A station carries one of nine states (`STATION_STATE_NAME`, plus the
 * `Started` the list returns in place of `Active`). Nine is more than either
 * the summary or the filter wants to show, so they are collapsed to four, and
 * both show the same four: offering one set in the filter while counting a
 * different set underneath read as two answers to the same question.
 *
 * A station is **running** when it is part-way through a cycle — started,
 * about to stop, or soaking between passes. Soaking is deliberately in here:
 * the station is mid-irrigation, not idle, even though nothing is flowing at
 * that instant. **Wait Start** is its own group: the station is committed to a
 * cycle it has not begun, which is the state an operator waiting on a program
 * is looking for, and lumping it in with the ones already flowing hid it.
 * **Error** is its own group for the same reason. Everything else is
 * **idling**: idle, inactive, or waiting on a schedule that has not come round
 * yet.
 */
export type StationStateBucket = 'running' | 'waitStart' | 'error' | 'idling'

export const STATION_STATE_BUCKETS: StationStateBucket[] = [
  'running',
  'waitStart',
  'error',
  'idling',
]

/** Tolgee key for each group's label, shared by the filter and the summary. */
export const STATION_STATE_BUCKET_LABEL: Record<StationStateBucket, string> = {
  running: 'stationsStateRunning',
  waitStart: 'stationsStateWaitStart',
  error: 'stationsStateError',
  idling: 'stationsStateIdling',
}

/**
 * States that count as running, lowercased for comparison.
 *
 * Matched exactly rather than by substring. The previous summary tested for
 * "start" or "run" anywhere in the name, which left `Wait Stop` — the same
 * cycle, one step later than `Started` — counted as idle. An exact list is the
 * only way to see the whole set at once and say whether it is right.
 */
const RUNNING_STATES = new Set([
  'started',
  'active',
  'running',
  'wait stop',
  'soaking',
  'rain soaking',
])

export const stationStateBucket = (state: unknown): StationStateBucket => {
  const name = String(state ?? '')
    .trim()
    .toLowerCase()
  if (name === 'error') return 'error'
  if (name === 'wait start') return 'waitStart'
  return RUNNING_STATES.has(name) ? 'running' : 'idling'
}

/**
 * The endpoint's `StateIds` for each group.
 *
 * These are the numbers `STATION_STATUS_NAME` maps (the 0-based StationState
 * SignalR sends), and the endpoint's filter takes the same enum. Confirmed
 * against staging on 2026-08-20: filtering by these ids returned 25 running,
 * 3 error and 463 idling, matching a client-side pass over all 491 rows
 * exactly, and summing to 491 — so no state falls outside the groups. Wait
 * Start (id 3) has since been split out of running; the ids it covers are
 * unchanged, only which group they are counted under.
 *
 * `Active` (the name the enum uses where the list says `Started`) has no id of
 * its own; 4 covers both.
 */
export const STATION_STATE_BUCKET_IDS: Record<StationStateBucket, number[]> = {
  // Started, Wait Stop, Soaking, Rain Soaking.
  running: [4, 5, 6, 8],
  // Wait Start.
  waitStart: [3],
  // Error.
  error: [7],
  // Inactive, Idling, Wait Schedule.
  idling: [0, 1, 2],
}
